import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import T from '@/components/T';

interface SubscriptionStatusBadgeProps {
  collapsed: boolean;
}

export const SubscriptionStatusBadge: React.FC<SubscriptionStatusBadgeProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const { plans, currentSubscription, loading } = useSubscriptionPlans();

  // Показываем лоадер пока загружается
  if (loading) {
    if (collapsed) {
      return (
        <div className="flex items-center justify-center p-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      );
    }
    return (
      <div className="rounded-lg p-3 border border-border bg-muted/30 animate-pulse">
        <div className="h-4 bg-muted rounded w-20 mb-2" />
        <div className="h-5 bg-muted rounded w-32 mb-1" />
        <div className="h-3 bg-muted rounded w-24" />
      </div>
    );
  }

  const currentPlan = plans.find(plan => plan.id === currentSubscription?.plan_id);
  // ⚡ AK-47: действующая подписка = expires_at в будущем или null.
  // Статус 'pending' (оплата в обработке) при валидном сроке НЕ блокирует доступ.
  const hasActiveSubscription = !!currentSubscription && (
    currentSubscription.expires_at === null ||
    new Date(currentSubscription.expires_at) > new Date()
  );
  const isPaymentPending = hasActiveSubscription && currentSubscription?.status === 'pending';
  
  const daysUntilExpiry = currentSubscription?.expires_at 
    ? Math.ceil((new Date(currentSubscription.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Бессрочная подписка (expires_at = null, но статус active)
  const isUnlimited = hasActiveSubscription && !currentSubscription?.expires_at;
  
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;

  const getDaysText = (days: number) => {
    if (days <= 0) return 'Истёк';
    if (days === 1) return '1 день';
    if (days >= 2 && days <= 4) return `${days} дня`;
    return `${days} дней`;
  };

  const handleClick = () => {
    navigate('/subscription/payment');
  };

  if (collapsed) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        className={`w-full ${
          !hasActiveSubscription || isExpired 
            ? 'text-destructive hover:text-destructive' 
            : isExpiringSoon 
              ? 'text-warning hover:text-warning'
              : 'text-primary hover:text-primary'
        }`}
        title={currentPlan?.name || 'Подписка'}
      >
        <Crown className="h-5 w-5" />
      </Button>
    );
  }

  // Нет активной подписки
  if (!hasActiveSubscription) {
    return (
      <div 
        className="rounded-lg p-3 border cursor-pointer transition-colors border-destructive bg-destructive/10 hover:bg-destructive/20"
        onClick={handleClick}
      >
        <div className="flex items-center gap-2 mb-2">
          <Crown className="h-4 w-4 text-destructive" />
          <span className="text-xs font-medium text-muted-foreground">
            <T ru="Тариф" en="Plan" />
          </span>
        </div>
        
        <p className="font-semibold text-sm mb-1 text-destructive">
          <T ru="Нет подписки" en="No subscription" />
        </p>
        
        <Button 
          size="sm" 
          variant="destructive"
          className="w-full text-xs h-7"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          <CreditCard className="h-3 w-3 mr-1.5" />
          <T ru="Оформить" en="Subscribe" />
        </Button>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-lg p-3 border cursor-pointer transition-colors ${
        isExpired 
          ? 'border-destructive bg-destructive/10 hover:bg-destructive/20' 
          : isExpiringSoon 
            ? 'border-warning bg-warning/10 hover:bg-warning/20' 
            : 'border-primary/30 bg-primary/5 hover:bg-primary/10'
      }`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 mb-2">
        <Crown className={`h-4 w-4 ${
          isExpired ? 'text-destructive' : isExpiringSoon ? 'text-warning' : 'text-primary'
        }`} />
        <span className="text-xs font-medium text-muted-foreground">
          <T ru="Тариф" en="Plan" />
        </span>
      </div>
      
      <p className="font-semibold text-sm mb-1">
        {currentPlan?.name || currentSubscription?.plan?.name || 'Активная подписка'}
      </p>

      {isPaymentPending && (
        <p className="text-xs mb-1 text-warning font-medium">
          <T ru="Оплата в обработке" en="Payment processing" />
        </p>
      )}

      {isUnlimited ? (
        <p className="text-xs mb-2 text-primary">
          <T ru="Бессрочная" en="Unlimited" />
        </p>
      ) : daysUntilExpiry !== null && (
        <p className={`text-xs mb-2 ${
          isExpired ? 'text-destructive' : isExpiringSoon ? 'text-warning' : 'text-muted-foreground'
        }`}>
          <T ru="Осталось:" en="Remaining:" /> {getDaysText(daysUntilExpiry)}
        </p>
      )}
      
      <Button 
        size="sm" 
        variant={isExpired ? "destructive" : isExpiringSoon ? "outline" : "outline"}
        className="w-full text-xs h-7"
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
      >
        <CreditCard className="h-3 w-3 mr-1.5" />
        <T ru={isExpired ? "Продлить" : "Управление"} en={isExpired ? "Renew" : "Manage"} />
      </Button>
    </div>
  );
};
